
import { useMemo, useState } from 'react';
import { DebtEvent, DebtRecord, Property, Portfolio, PropertyDebtLink } from '../types';
import { formatCurrency0, formatPercent2, formatNumber2 } from '../utils/format';
import { DollarSign, Percent, TrendingUp, Layers, X, BarChart3, Table as TableIcon, Zap, AlertTriangle, Clock } from 'lucide-react';
import SummaryCard from './SummaryCard';
import PortfolioSpreadsheetView from './PortfolioSpreadsheetView';
import { buildModel } from '../services/financials';
import { buildDebtSnapshotForProperty } from '../services/debt';
import { computeRiskSummary } from '../services/risk';

interface PortfolioViewProps {
  portfolio: Portfolio;
  properties: Record<string, Property>;
  debts: Record<string, DebtRecord>;
  debtLinks: PropertyDebtLink[];
  debtEvents: DebtEvent[];
  onRemoveProperty: (id: string) => void;
}

const PortfolioView = (props: PortfolioViewProps) => {
  const { portfolio, properties, debts, debtLinks, debtEvents, onRemoveProperty } = props;
  const [activeTab, setActiveTab] = useState('dashboard' as 'dashboard' | 'spreadsheet' | 'risk');
  const [selectedYear, setSelectedYear] = useState(1);

  const portfolioProperties = useMemo(() =>
    portfolio.propertyIds.map((id: any) => properties[id]).filter(Boolean),
    [portfolio.propertyIds, properties]
  );

  const portfolioEntityLabel = useMemo(() => {
    const values = new Set<string>();
    portfolioProperties.forEach((property: Property) => {
      const entity = (property.ownershipEntity || '').trim();
      if (entity) values.add(entity);
    });
    const entities = [...values];
    if (entities.length === 1) return entities[0];
    if (entities.length > 1) return 'Multiple entities';
    return null;
  }, [portfolioProperties]);

  const propertyModels = useMemo(() => (
    portfolioProperties.map((p: any) => {
      const propertyInputs = p.currentYearInputs || p.inputs;
      const debtSnapshot = buildDebtSnapshotForProperty(
        p.id,
        properties,
        debts,
        debtLinks,
        debtEvents,
        undefined,
        { includeSharedPortfolioDebt: true }
      );
      const model = buildModel({ ...propertyInputs }, p.riskProfile, debtSnapshot, { baseYear: p.baseYear, historicalActuals: p.historicalActuals }, { modelEvents: p.modelEvents || [] });
      return {
        property: p,
        model,
        summary: computeRiskSummary(propertyInputs, p.riskProfile, model)
      };
    })
  ), [portfolioProperties, properties, debts, debtLinks, debtEvents]);

  const maxAnalysisYear = useMemo(() => (
    propertyModels.reduce((max: number, entry: any) => Math.max(max, entry.model.proForma.length || 0), 1)
  ), [propertyModels]);

  const selectedYearIndex = Math.max(0, Math.min(maxAnalysisYear - 1, selectedYear - 1));
  const getPropertyYearRow = (entry: any) => {
    const propertyInputs = entry.property.currentYearInputs || entry.property.inputs;
    return entry.model.proForma[selectedYearIndex] || entry.model.proForma[entry.model.proForma.length - 1] || {
      assumedSalePrice: propertyInputs.purchasePrice || 0,
      loanBalance: entry.model.metrics.allocatedCurrentLoanBalance || 0,
      operatingCf: 0
    };
  };

  const getPropertyModeledValue = (entry: any) => {
    const propertyInputs = entry.property.currentYearInputs || entry.property.inputs;
    return getPropertyYearRow(entry)?.assumedSalePrice || propertyInputs.purchasePrice;
  };

  const stats = useMemo(() => {
    if (portfolioProperties.length === 0) return null;

    const totalPurchaseBasis = portfolioProperties.reduce((sum: any, p: any) => {
      const propertyInputs = p.currentYearInputs || p.inputs;
      return sum + (propertyInputs.purchasePrice || 0);
    }, 0);
    const totalEquityInvested = propertyModels.reduce((sum: any, entry: any) => sum + entry.model.metrics.totalInitialInvestment, 0);
    const totalDebt = propertyModels.reduce((sum: any, entry: any) => sum + (getPropertyYearRow(entry)?.loanBalance || 0), 0);
    const totalCurrentValue = propertyModels.reduce((sum: any, entry: any) => sum + getPropertyModeledValue(entry), 0);
    const totalEquityValue = propertyModels.reduce((sum: any, entry: any) => {
      const currentValue = getPropertyModeledValue(entry);
      const debt = getPropertyYearRow(entry)?.loanBalance || 0;
      return sum + (currentValue - debt);
    }, 0);
    const totalNetSales = propertyModels.reduce((sum: any, entry: any) => sum + entry.model.metrics.netSalesProceeds, 0);
    const totalGPR = portfolioProperties.reduce((sum: any, p: any) => {
      const propertyInputs = p.currentYearInputs || p.inputs;
      return sum + (propertyInputs.annualRent || 0);
    }, 0);
    const totalReserves = portfolioProperties.reduce((sum: any, p: any) => {
      const propertyInputs = p.currentYearInputs || p.inputs;
      return sum + (propertyInputs.propertyReserves ?? 0) + (propertyInputs.portfolioReservesAllocated ?? 0);
    }, 0);
    const totalRecommendedReserves = propertyModels.reduce((sum: any, entry: any) => sum + entry.summary.recommendedReserve, 0);
    const totalReserveGap = Math.max(0, totalRecommendedReserves - totalReserves);
    const totalMonthlyFixedOutflow = propertyModels.reduce((sum: any, entry: any) => sum + entry.summary.monthlyFixedOutflow, 0);
    const portfolioRunwayMonths = totalReserves / Math.max(1, totalMonthlyFixedOutflow);
    const highestRiskAsset = [...propertyModels].sort((a: any, b: any) => b.summary.overallRiskScore - a.summary.overallRiskScore)[0];
    const highestExposureAsset = [...propertyModels].sort((a: any, b: any) => b.summary.capexNext24Months - a.summary.capexNext24Months)[0];

    const irrEligibleProperties = propertyModels.filter((entry: any) => getPropertyYearRow(entry)?.irr !== null);
    const irrEligibleEquity = irrEligibleProperties.reduce((sum: any, entry: any) => sum + entry.model.metrics.totalInitialInvestment, 0);
    const weightedIrr = irrEligibleProperties.length === 0 ? null : irrEligibleProperties.reduce((sum: any, entry: any) => {
      const weight = entry.model.metrics.totalInitialInvestment / Math.max(1, irrEligibleEquity);
      return sum + (((getPropertyYearRow(entry)?.irr) || 0) * weight);
    }, 0);

    const weightedCoC = propertyModels.reduce((sum: any, entry: any) => {
      const weight = entry.model.metrics.totalInitialInvestment / Math.max(1, totalEquityInvested);
      return sum + (entry.model.metrics.year1CoC * weight);
    }, 0);

    const weightedMoic = propertyModels.reduce((sum: any, entry: any) => {
      const weight = entry.model.metrics.totalInitialInvestment / Math.max(1, totalEquityInvested);
      return sum + (entry.model.metrics.moic * weight);
    }, 0);

    const avgLTV = (totalDebt / Math.max(1, totalCurrentValue)) * 100;
    const annualCashFlow = propertyModels.reduce((sum: any, entry: any) => {
      return sum + (getPropertyYearRow(entry)?.operatingCf || 0);
    }, 0);
    const monthsOfReserveCoverage = portfolioRunwayMonths;
    const portfolioRiskScore = propertyModels.reduce((sum: any, entry: any) => {
      const weight = entry.model.metrics.totalInitialInvestment / Math.max(1, totalEquityInvested);
      return sum + (entry.summary.overallRiskScore * weight);
    }, 0);
    const keyWarning = highestRiskAsset?.summary.actions[0]?.detail
      || (totalReserveGap > 0 ? `You are under-reserved by ${formatCurrency0(totalReserveGap)} across the portfolio.` : null)
      || 'No urgent portfolio-wide warning is currently flagged.';

    return {
      totalPurchaseBasis,
      totalCurrentValue,
      totalEquityInvested,
      totalDebt,
      totalEquityValue,
      totalNetSales,
      totalGPR,
      weightedIrr,
      weightedCoC,
      weightedMoic,
      avgLTV,
      annualCashFlow,
      monthsOfReserveCoverage,
      totalReserves,
      totalRecommendedReserves,
      totalReserveGap,
      totalMonthlyFixedOutflow,
      portfolioRunwayMonths,
      portfolioRiskScore,
      highestRiskAsset,
      highestExposureAsset,
      keyWarning
    };
  }, [portfolioProperties, propertyModels, selectedYearIndex]);
  if (!stats) return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400 bg-slate-900 rounded-3xl border-2 border-dashed border-slate-800">
      <Layers size={48} className="mb-4 opacity-10" />
      <div className="text-xl font-bold text-slate-300">This Portfolio is Empty</div>
      <p className="text-sm mt-1 max-w-xs text-center px-4">Drag properties from the Inventory list into this portfolio in the sidebar to start tracking aggregated metrics.</p>
    </div>
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-white tracking-tight">{portfolio.name}</h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-400 text-sm font-medium">
              Aggregated analysis of {portfolioProperties.length} properties{portfolioEntityLabel ? ` in ${portfolioEntityLabel}` : ''}.
            </p>
          </div>
        </div>

      <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'dashboard' ? 'bg-slate-50 text-slate-900 border border-slate-200' : 'text-slate-500 hover:text-slate-900'}`}
          >
            <BarChart3 size={16} /> Dashboard
          </button>
          <button
            onClick={() => setActiveTab('risk')}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'risk' ? 'bg-slate-50 text-slate-900 border border-slate-200' : 'text-slate-500 hover:text-slate-900'}`}
          >
            <Zap size={16} /> Risk
          </button>
          <button
            onClick={() => setActiveTab('spreadsheet')}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'spreadsheet' ? 'bg-slate-50 text-slate-900 border border-slate-200' : 'text-slate-500 hover:text-slate-900'}`}
          >
            <TableIcon size={16} /> Spreadsheet
          </button>
        </div>
      </div>
      </div>

      {activeTab === 'dashboard' ? (
        <>
          <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-medium text-slate-500">
              Shared debt assigned to this portfolio’s properties is reflected analytically in the property-level metrics below. Original debt records stay intact in Debt Desk and tax reporting.
            </p>
          </div>

          <div className="mb-10 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Portfolio Snapshot</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">This view is focused on the decision: are you safe, under-reserved, overleveraged, and which asset needs attention first?</p>
              </div>
              <span className={`inline-flex rounded-full border px-3 py-1.5 text-[9px] font-black uppercase tracking-widest ${
                stats.portfolioRiskScore >= 70
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                  : stats.portfolioRiskScore >= 45
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                    : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
              }`}>
                {stats.portfolioRiskScore >= 70 ? 'Elevated Risk' : stats.portfolioRiskScore >= 45 ? 'Moderate Risk' : 'Stable'}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-medium text-slate-500">Overall Risk Score</p>
                    <p className={`mt-2 text-2xl font-semibold ${
                      stats.portfolioRiskScore >= 70 ? 'text-rose-300' : stats.portfolioRiskScore >= 45 ? 'text-amber-300' : 'text-emerald-300'
                    }`}>{formatNumber2(stats.portfolioRiskScore)} / 100</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Weighted from each asset’s operating, reserve, and capital risk profile.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-medium text-slate-500">Cash Runway</p>
                    <p className={`mt-2 text-2xl font-semibold ${
                      stats.portfolioRunwayMonths < 3 ? 'text-rose-300' : stats.portfolioRunwayMonths < 6 ? 'text-amber-300' : 'text-emerald-300'
                    }`}>{formatNumber2(stats.portfolioRunwayMonths)} months</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Portfolio reserves divided by fixed monthly outflow.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-medium text-slate-500">Highest Risk Asset</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.highestRiskAsset?.property.name || 'N/A'}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Risk score {formatNumber2(stats.highestRiskAsset?.summary.overallRiskScore || 0)} with the weakest near-term operating posture.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-medium text-slate-500">Reserve Gap</p>
                    <p className={`mt-2 text-2xl font-semibold ${stats.totalReserveGap > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>{stats.totalReserveGap > 0 ? formatCurrency0(stats.totalReserveGap) : 'Fully Funded'}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{stats.totalReserveGap > 0 ? 'Portfolio reserves are below the recommended cushion.' : 'Current reserves cover the recommended portfolio cushion.'}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl border border-amber-200 bg-white p-3 text-amber-600">
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Key Warning</p>
                    <p className="mt-3 text-lg font-semibold text-slate-900">{stats.highestRiskAsset?.summary.actions[0]?.title || 'No urgent portfolio-wide action flagged'}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-500">{stats.keyWarning}</p>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-medium text-slate-500">What To Do Next</p>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {stats.totalReserveGap > 0
                      ? `Add roughly ${formatCurrency0(stats.totalReserveGap)} to reserves, then review ${stats.highestRiskAsset?.property.name || 'the highest-risk asset'} before expanding exposure.`
                      : `Pressure-test ${stats.highestRiskAsset?.property.name || 'the highest-risk asset'} first, then decide whether leverage or capital exposure is still above your comfort zone.`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <div className="mb-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Key Risks / Alerts</p>
              <p className="mt-2 text-sm text-slate-500">These are the main portfolio-level issues worth acting on right now.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="rounded-[1.75rem] border border-slate-700/60 bg-slate-900 p-5">
                <p className="inline-flex rounded-full border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-rose-300">Highest Risk Asset</p>
                <p className="mt-4 text-lg font-black text-white">{stats.highestRiskAsset?.property.name || 'N/A'}</p>
                <p className="mt-3 text-sm text-slate-400">{stats.highestRiskAsset?.summary.actions[0]?.detail || 'This asset currently carries the highest combined operating and reserve risk.'}</p>
              </div>
              <div className="rounded-[1.75rem] border border-slate-700/60 bg-slate-900 p-5">
                <p className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
                  stats.avgLTV > 65 ? 'border-rose-500/40 bg-rose-500/10 text-rose-300' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                }`}>Leverage Check</p>
                <p className="mt-4 text-lg font-black text-white">{formatPercent2(stats.avgLTV)} LTV</p>
                <p className="mt-3 text-sm text-slate-400">{stats.avgLTV > 65 ? 'Portfolio leverage is above a conservative threshold.' : 'Leverage is currently within a more conservative range.'}</p>
              </div>
              <div className="rounded-[1.75rem] border border-slate-700/60 bg-slate-900 p-5">
                <p className="inline-flex rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-amber-300">Biggest Upcoming Risk</p>
                <p className="mt-4 text-lg font-black text-white">{stats.highestExposureAsset?.property.name || 'N/A'}</p>
                <p className="mt-3 text-sm text-slate-400">{stats.highestExposureAsset ? `${formatCurrency0(stats.highestExposureAsset.summary.capexNext24Months)} of projected 24-month exposure is concentrated here.` : 'No material near-term exposure is currently flagged.'}</p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-500">Supporting Metrics</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">These roll up the selected analysis year so you can compare the portfolio at different points in the hold.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
              <SummaryCard label={`Year ${selectedYear} Modeled Value`} value={formatCurrency0(stats.totalCurrentValue)} subtext="Appreciation-based exit value for the selected year" icon={<Layers size={20} />} />
              <SummaryCard label={`Year ${selectedYear} Debt`} value={formatCurrency0(stats.totalDebt)} subtext="Total year-end debt across the portfolio" icon={<DollarSign size={20} />} />
              <SummaryCard label={`Year ${selectedYear} Equity`} value={formatCurrency0(stats.totalEquityValue)} subtext="Selected-year modeled value minus year-end debt" icon={<TrendingUp size={20} />} />
              <SummaryCard label={`Year ${selectedYear} Cash Flow`} value={formatCurrency0(stats.annualCashFlow)} subtext="Annual operating cash flow in the selected year" icon={<DollarSign size={20} />} />
              <SummaryCard label={`Year ${selectedYear} Weighted IRR`} value={formatPercent2(stats.weightedIrr)} subtext="Equity-weighted IRR through the selected year" icon={<Percent size={20} />} />
            </div>
          </div>

          <div className="saas-panel p-8">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <Layers size={12} className="text-slate-500" /> Detailed Data
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-500">Choose an analysis year, then inspect modeled value, year-end debt, equity, and annual cash flow for each property.</p>
              </div>
              <label className="flex flex-col gap-2 text-sm font-medium text-slate-500">
                Analysis Year
                <select
                  value={selectedYear}
                  onChange={(event: any) => setSelectedYear(Number(event.target.value))}
                  className="rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold tracking-normal text-white outline-none transition-colors focus:border-emerald-500"
                >
                  {Array.from({ length: maxAnalysisYear }, (_, index) => index + 1).map((year) => (
                    <option key={year} value={year}>
                      Year {year}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="pb-6">Property</th>
                    <th className="pb-6">{`Year ${selectedYear} Modeled Value`}</th>
                    <th className="pb-6">{`Year ${selectedYear} Debt`}</th>
                    <th className="pb-6">{`Year ${selectedYear} Equity`}</th>
                    <th className="pb-6">Risk Score</th>
                    <th className="pb-6">{`Year ${selectedYear} Cash Flow`}</th>
                    <th className="pb-6"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {propertyModels.map((entry: any) => {
                    const p = entry.property;
                    const yearRow = getPropertyYearRow(entry);
                    const currentValue = getPropertyModeledValue(entry);
                    const debt = yearRow?.loanBalance || 0;
                    const equity = currentValue - debt;
                    return (
                      <tr key={p.id} className="group transition-colors">
                        <td className="max-w-[180px] truncate py-6 text-sm font-semibold text-slate-900">{p.name || 'Unnamed'}</td>
                        <td className="py-6 text-slate-900">{formatCurrency0(currentValue)}</td>
                        <td className="py-6 text-rose-600">{formatCurrency0(debt)}</td>
                        <td className={`py-6 font-semibold ${equity >= 0 ? 'text-green-600' : 'text-rose-600'}`}>{formatCurrency0(equity)}</td>
                        <td className={`py-6 font-semibold ${entry.summary.overallRiskScore >= 70 ? 'text-rose-600' : entry.summary.overallRiskScore >= 45 ? 'text-amber-600' : 'text-green-600'}`}>{formatNumber2(entry.summary.overallRiskScore)}</td>
                        <td className={`py-6 font-semibold ${(yearRow?.operatingCf || 0) >= 0 ? 'text-green-600' : 'text-rose-600'}`}>{formatCurrency0(yearRow?.operatingCf || 0)}</td>
                        <td className="py-6">
                          <button
                            onClick={() => onRemoveProperty(p.id)}
                            className="rounded-lg p-1.5 text-slate-400 transition-all hover:bg-slate-100 hover:text-rose-600"
                            title="Remove from Portfolio"
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeTab === 'risk' ? (
        <PortfolioRiskView portfolioProperties={portfolioProperties} stats={stats} />
      ) : (
        <PortfolioSpreadsheetView properties={portfolioProperties} allProperties={properties} debts={debts} debtLinks={debtLinks} debtEvents={debtEvents} />
      )}
    </div>
  );
};

// Portfolio Risk Dashboard Component
const PortfolioRiskView = ({ portfolioProperties, stats }: any) => {
  const monthlyPortfolioCashFlow = Number(stats?.annualCashFlow ?? 0) / 12;
  // Recovery time only makes sense when the portfolio is generating positive monthly cash flow.
  const getRecoveryLabel = (impactAmount: number) => {
    if (monthlyPortfolioCashFlow <= 0) return 'N/A';
    const monthsToRecover = Math.abs(impactAmount) / monthlyPortfolioCashFlow;
    return `${monthsToRecover.toFixed(1)} months`;
  };

  const vacancyRecoveryLabel = getRecoveryLabel(-(stats.totalGPR / 12));
  const unexpectedRepairRecoveryLabel = getRecoveryLabel(-5000);
  const rentDeclineRecoveryLabel = getRecoveryLabel(-(stats.totalGPR * 0.05));

  const riskScore = useMemo(() => {
    if (!stats) return 0;
    let score = 0;
    // Leverage risk (higher LTV = higher risk)
    score += Math.min(40, (stats.avgLTV / 100) * 40);
    // Reserve coverage (lower = higher risk)
    score += Math.max(0, 30 - (stats.monthsOfReserveCoverage * 5));
    // Negative cash flow risk
    if (stats.annualCashFlow < 0) score += 30;
    return Math.min(100, score);
  }, [stats]);

  const getRiskLabel = (score: number) => {
    if (score < 30) return 'Low';
    if (score < 60) return 'Moderate';
    return 'Elevated';
  };

  const getRiskColor = (score: number) => {
    if (score < 30) return 'text-emerald-400 bg-emerald-950/30 border-emerald-800/50';
    if (score < 60) return 'text-amber-400 bg-amber-950/30 border-amber-800/50';
    return 'text-rose-400 bg-rose-950/30 border-rose-800/50';
  };

  return (
    <div className="space-y-10">
      {/* Top 4 Risk Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className={`p-8 rounded-[2.5rem] border-2 flex flex-col justify-between ${getRiskColor(riskScore)}`}>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Portfolio Risk Score</p>
            <p className="text-4xl font-black tracking-tighter">{getRiskLabel(riskScore)}</p>
            <p className="text-[10px] font-medium text-slate-400 mt-4 leading-relaxed">Based on leverage, reserves, cash flow cushion, and expense sensitivity</p>
          </div>
        </div>

        <SummaryCard
          label="Reserve Coverage"
          value={`${formatNumber2(stats.monthsOfReserveCoverage)}mo`}
          subtext="Months of expense coverage"
          icon={<Clock size={20} />}
        />

        <SummaryCard
          label="Blended Leverage"
          value={formatPercent2(stats.avgLTV)}
          subtext="Debt to portfolio value"
          icon={<DollarSign size={20} />}
        />

        <SummaryCard
          label="Annual Cash Flow"
          value={formatCurrency0(stats.annualCashFlow)}
          subtext="Portfolio-level net CF"
          icon={<TrendingUp size={20} />}
        />
      </div>

      {/* Risk Breakdown Section */}
      <div className="bg-slate-800 p-8 rounded-[3rem] border border-slate-700/50 shadow-xl shadow-black/30">
        <h3 className="text-[10px] font-black text-slate-400 mb-8 uppercase tracking-[0.2em] flex items-center gap-2">
          <AlertTriangle size={12} className="text-amber-500" /> Risk Breakdown
        </h3>

        <div className="space-y-6">
          {/* Leverage Risk */}
          <div className="flex items-center justify-between pb-6 border-b border-slate-700/50">
            <div>
              <p className="text-sm font-black text-white">Leverage Risk</p>
              <p className="text-[9px] text-slate-400 font-medium mt-1">Debt is {formatPercent2(stats.avgLTV)} of portfolio value</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${stats.avgLTV > 70 ? 'bg-rose-500' : stats.avgLTV > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, stats.avgLTV)}%` }}
                />
              </div>
              <span className={`text-sm font-black w-12 text-right ${stats.avgLTV > 70 ? 'text-rose-400' : stats.avgLTV > 50 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {stats.avgLTV > 70 ? 'High' : stats.avgLTV > 50 ? 'Moderate' : 'Low'}
              </span>
            </div>
          </div>

          {/* Reserve Coverage Risk */}
          <div className="flex items-center justify-between pb-6 border-b border-slate-700/50">
            <div>
              <p className="text-sm font-black text-white">Cash Reserve cushion</p>
              <p className="text-[9px] text-slate-400 font-medium mt-1">{formatNumber2(stats.monthsOfReserveCoverage)} months of coverage</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${stats.monthsOfReserveCoverage < 2 ? 'bg-rose-500' : stats.monthsOfReserveCoverage < 4 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, (stats.monthsOfReserveCoverage / 12) * 100)}%` }}
                />
              </div>
              <span className={`text-sm font-black w-12 text-right ${stats.monthsOfReserveCoverage < 2 ? 'text-rose-400' : stats.monthsOfReserveCoverage < 4 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {stats.monthsOfReserveCoverage < 2 ? 'Low' : stats.monthsOfReserveCoverage < 4 ? 'Moderate' : 'Strong'}
              </span>
            </div>
          </div>

          {/* Cash Flow Risk */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-black text-white">Annual Cash Flow Health</p>
              <p className="text-[9px] text-slate-400 font-medium mt-1">{formatCurrency0(stats.annualCashFlow)} net annual</p>
            </div>
            <span className={`text-sm font-black ${stats.annualCashFlow > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {stats.annualCashFlow > 0 ? 'Positive' : 'Negative'}
            </span>
          </div>
        </div>
      </div>

      {/* Stress Test Section */}
      <div className="bg-slate-800 rounded-[3rem] border border-slate-700/50 shadow-xl shadow-black/30 overflow-hidden">
        <div className="p-8 border-b border-slate-700/50 flex justify-between items-center">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
            <Zap size={12} className="text-amber-500" /> Stress Test Scenarios
          </h3>
          <span className="text-[9px] font-black bg-slate-900/50 px-3 py-1 rounded-full text-slate-400 uppercase tracking-widest">Simulated Impact</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-900/30 text-slate-400 font-black uppercase text-[9px] tracking-[0.15em] border-b border-slate-700/50">
                <th className="px-8 py-6">Scenario</th>
                <th className="px-6 py-6 text-right">Impact on Cash Flow</th>
                <th className="px-6 py-6 text-right">Recovery Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              <tr className="hover:bg-slate-700/20 transition-colors">
                <td className="px-8 py-6">
                  <div>
                    <p className="font-black text-white">1 Month Vacancy</p>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Lost rental income</p>
                  </div>
                </td>
                <td className="px-6 py-6 text-right">
                  <p className="font-black text-rose-400 text-base">{formatCurrency0(-(stats.totalGPR / 12))}</p>
                </td>
                <td className="px-6 py-6 text-right">
                  <p className={`font-black ${vacancyRecoveryLabel === 'N/A' ? 'text-slate-400' : 'text-amber-400'}`}>
                    {vacancyRecoveryLabel}
                  </p>
                </td>
              </tr>
              <tr className="hover:bg-slate-700/20 transition-colors">
                <td className="px-8 py-6">
                  <div>
                    <p className="font-black text-white">Unexpected Repair</p>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">$5,000 one-time cost</p>
                  </div>
                </td>
                <td className="px-6 py-6 text-right">
                  <p className="font-black text-rose-400 text-base">-$5,000</p>
                </td>
                <td className="px-6 py-6 text-right">
                      <p className={`font-black ${unexpectedRepairRecoveryLabel === 'N/A' ? 'text-slate-400' : 'text-amber-400'}`}>
                        {unexpectedRepairRecoveryLabel}
                      </p>
                </td>
              </tr>
              <tr className="hover:bg-slate-700/20 transition-colors">
                <td className="px-8 py-6">
                  <div>
                    <p className="font-black text-white">5% Rent Decline</p>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Transient market weakness</p>
                  </div>
                </td>
                <td className="px-6 py-6 text-right">
                  <p className="font-black text-rose-400 text-base">{formatCurrency0(-(stats.totalGPR * 0.05))}</p>
                </td>
                <td className="px-6 py-6 text-right">
                  <p className={`font-black ${rentDeclineRecoveryLabel === 'N/A' ? 'text-slate-400' : 'text-amber-400'}`}>
                    {rentDeclineRecoveryLabel}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Portfolio Resilience Insight */}
      <div className="bg-slate-900 border border-slate-700/50 rounded-[2.5rem] p-8 shadow-xl shadow-black/30">
        <h3 className="text-[10px] font-black text-emerald-400 mb-4 uppercase tracking-[0.2em]">Portfolio Insight</h3>
        <p className="text-sm text-slate-300 leading-relaxed font-medium">
          {riskScore < 30
            ? `This portfolio demonstrates strong resilience with manageable leverage (${formatPercent2(stats.avgLTV)}) and ${formatNumber2(stats.monthsOfReserveCoverage)} months of expense coverage. Cash flow remains positive across all properties, providing a reliable cushion against common disruptions.`
            : riskScore < 60
            ? `This portfolio shows balanced performance with moderate leverage and reasonable reserves. Under stress scenarios, focus on maintaining adequate liquidity reserves to weather unexpected repairs or temporary vacancy. Consider stabilizing cash flow before major rebalancing.`
            : `This portfolio carries elevated risk due to high leverage and limited reserve coverage. Prioritize building cash reserves and optimizing cash flow. A single major expense or vacancy event could strain capacity. Review exit opportunities or consider refinancing to reduce debt exposure.`
          }
        </p>
      </div>
    </div>
  );
};

export default PortfolioView;
